import type { GridCell, Rectangle } from "@glideapps/glide-data-grid";
import dayjs from "dayjs";
import {
  AI_SPREADSHEET_VALUE_TYPE,
  type AiSpreadsheetValueType,
  type IAiSpreadsheetCell,
  type IAiSpreadsheetRegion,
} from "@/payroll/ai-script/types";
import type { AdaptedSpreadsheetSheet } from "./adapter";
import { getAiSpreadsheetCellDisplayValue } from "./cell-display-value";

// The API reports a column whose nonblank displayed values disagree as `mixed`
// (`column_type_policy.mixed_fallback`); it is not a storable cell format, so it
// lives alongside AiSpreadsheetValueType rather than inside it.
export const SPREADSHEET_REGION_MIXED_COLUMN_TYPE = "mixed" as const;

export type SpreadsheetRegionColumnType =
  | AiSpreadsheetValueType
  | typeof SPREADSHEET_REGION_MIXED_COLUMN_TYPE;

export type SpreadsheetFilterCondition =
  | "contains"
  | "equals"
  | "greater_than"
  | "less_than";

export interface SpreadsheetColumnFilter {
  selectedValues: string[] | null;
  condition: SpreadsheetFilterCondition | null;
  conditionValue: string;
}

export interface SpreadsheetRegionSort {
  column: number;
  direction: "asc" | "desc";
}

export interface SpreadsheetRegionViewState {
  filters: Record<string, SpreadsheetColumnFilter>;
  sort: SpreadsheetRegionSort | null;
}

export type SpreadsheetRegionViewStates = Record<
  string,
  SpreadsheetRegionViewState
>;

export const getSavedSpreadsheetRow = (rowMap: number[], displayRow: number) =>
  rowMap[displayRow] ?? displayRow;

export const getSavedRowInsertAnchor = (
  rowMap: number[],
  displayRow: number,
  position: "above" | "below",
) =>
  getSavedSpreadsheetRow(rowMap, displayRow) + (position === "below" ? 1 : 0);

export const findDisplayedSpreadsheetRow = (
  rowMap: number[],
  savedRow: number,
): number | null => {
  const displayRow = rowMap.indexOf(savedRow);
  return displayRow === -1 ? null : displayRow;
};

export const emptySpreadsheetRegionViewState =
  (): SpreadsheetRegionViewState => ({ filters: {}, sort: null });

const displayValue = (cell: IAiSpreadsheetCell | undefined) =>
  cell ? getAiSpreadsheetCellDisplayValue(cell) : "";

const normalizeComparableValue = (
  value: string,
  type: AiSpreadsheetValueType | null,
): string | number => {
  if (type === "number" || type === "currency" || type === "percent") {
    const number = Number(value.replace(/[$,%\s,]/g, ""));
    return Number.isNaN(number) ? value.toLocaleLowerCase() : number;
  }
  if (type === "date") {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.valueOf() : value.toLocaleLowerCase();
  }
  if (type === "boolean") return value.toLocaleLowerCase() === "true" ? 1 : 0;
  return value.toLocaleLowerCase();
};

const isInvalidTypedComparableValue = (
  value: string | number,
  type: AiSpreadsheetValueType,
) =>
  typeof value === "string" &&
  (type === AI_SPREADSHEET_VALUE_TYPE.number ||
    type === AI_SPREADSHEET_VALUE_TYPE.currency ||
    type === AI_SPREADSHEET_VALUE_TYPE.percent ||
    type === AI_SPREADSHEET_VALUE_TYPE.date);

const matchesCondition = (
  value: string,
  filter: SpreadsheetColumnFilter,
  type: AiSpreadsheetValueType | null,
) => {
  if (!filter.condition || filter.conditionValue === "") return true;
  // Blank rows are selected through the value checklist. They must not become
  // zero, false, or the Unix epoch during a typed condition comparison.
  if (value === "") return false;

  if (filter.condition === "contains") {
    return value
      .toLocaleLowerCase()
      .includes(filter.conditionValue.toLocaleLowerCase());
  }

  const comparable = normalizeComparableValue(value, type);
  const target = normalizeComparableValue(filter.conditionValue, type);

  if (filter.condition === "equals") return comparable === target;
  if (filter.condition === "greater_than") return comparable > target;
  return comparable < target;
};

const cellsByRowAndColumn = (cells: IAiSpreadsheetCell[]) =>
  new Map(cells.map((cell) => [`${cell.row}:${cell.col}`, cell]));

const isValueType = (value: string | null): value is AiSpreadsheetValueType =>
  value != null && value in AI_SPREADSHEET_VALUE_TYPE;

/**
 * Mirrors the API's `column_type_policy` (WorkbookSerializer::COLUMN_TYPE_POLICY):
 * a declared type wins, otherwise the displayed record-cell values decide, blanks
 * are ignored, and a column whose nonblank values disagree resolves to `mixed`.
 * Kept as a distinct value rather than collapsing to `text` so the column menu can
 * say the type is ambiguous; comparison coerces it to text via `comparableType`.
 */
export const resolveSpreadsheetRegionColumnType = (
  cells: IAiSpreadsheetCell[],
  region: IAiSpreadsheetRegion,
  column: number,
): SpreadsheetRegionColumnType => {
  const declared = region.column_settings[String(column)]?.declared_type;
  if (declared) return declared;

  const types = new Set(
    cells
      .filter(
        (cell) =>
          cell.col === column &&
          cell.row >= region.record_start_row &&
          cell.row <= region.record_end_row &&
          displayValue(cell) !== "" &&
          cell.value_type != null,
      )
      .map((cell) => cell.value_type),
  );
  if (types.size === 0) return AI_SPREADSHEET_VALUE_TYPE.text;
  if (types.size > 1) return SPREADSHEET_REGION_MIXED_COLUMN_TYPE;

  const [type] = Array.from(types);
  return isValueType(type) ? type : AI_SPREADSHEET_VALUE_TYPE.text;
};

// `mixed` has no ordering of its own, so sorting and conditions treat it as text.
const comparableType = (
  type: SpreadsheetRegionColumnType,
): AiSpreadsheetValueType =>
  type === SPREADSHEET_REGION_MIXED_COLUMN_TYPE
    ? AI_SPREADSHEET_VALUE_TYPE.text
    : type;

export const getSpreadsheetRegionColumnValues = (
  cells: IAiSpreadsheetCell[],
  region: IAiSpreadsheetRegion,
  column: number,
) => {
  const byCoordinate = cellsByRowAndColumn(cells);
  return Array.from(
    new Set(
      Array.from(
        { length: region.record_end_row - region.record_start_row + 1 },
        (_, offset) =>
          displayValue(
            byCoordinate.get(`${region.record_start_row + offset}:${column}`),
          ),
      ),
    ),
  ).sort((left, right) => left.localeCompare(right));
};

const projectRegionRows = (
  cells: IAiSpreadsheetCell[],
  region: IAiSpreadsheetRegion,
  state: SpreadsheetRegionViewState | undefined,
) => {
  const rows = Array.from(
    { length: region.record_end_row - region.record_start_row + 1 },
    (_, offset) => region.record_start_row + offset - 1,
  );
  if (!state) return rows;

  const byCoordinate = cellsByRowAndColumn(cells);

  /**
   * Everything that depends only on the COLUMN is resolved once, before the row
   * loop. Resolving the column type per row instead costs rows × filters × every
   * cell on the sheet, because the type is read by scanning the cell list — on a
   * large tab that is tens of millions of comparisons for a single filter change.
   * Selected values become a Set for the same reason: membership per row was a
   * linear scan of the kept-values list.
   */
  const activeFilters = Object.entries(state.filters).map(
    ([columnKey, filter]) => {
      const column = Number(columnKey);
      return {
        column,
        filter,
        selected:
          filter.selectedValues === null
            ? null
            : new Set(filter.selectedValues),
        type: comparableType(
          resolveSpreadsheetRegionColumnType(cells, region, column),
        ),
      };
    },
  );

  const filtered = rows.filter((zeroBasedRow) =>
    activeFilters.every(({ column, filter, selected, type }) => {
      const value = displayValue(
        byCoordinate.get(`${zeroBasedRow + 1}:${column}`),
      );
      if (selected !== null && !selected.has(value)) return false;
      return matchesCondition(value, filter, type);
    }),
  );

  if (!state.sort) return filtered;
  const { column, direction } = state.sort;
  const type = comparableType(
    resolveSpreadsheetRegionColumnType(cells, region, column),
  );

  // Decorate-sort-undecorate: each row's comparable value is parsed once rather
  // than on every one of the O(n log n) comparisons.
  return filtered
    .map((row) => {
      const text = displayValue(byCoordinate.get(`${row + 1}:${column}`));
      const value = normalizeComparableValue(text, type);
      return {
        row,
        text,
        value,
        invalid: isInvalidTypedComparableValue(value, type),
      };
    })
    .sort((left, right) => {
      if (left.text === right.text) return left.row - right.row;
      // Blanks sink to the bottom in both directions, so this comparison stays
      // outside the direction flip.
      if (left.text === "") return 1;
      if (right.text === "") return -1;
      // Invalid typed values follow valid values in both directions. Comparing a
      // number with an invalid string would otherwise coerce the string to NaN.
      if (left.invalid !== right.invalid) return left.invalid ? 1 : -1;
      if (left.value === right.value) return left.row - right.row;
      const result = left.value < right.value ? -1 : 1;
      return direction === "asc" ? result : -result;
    })
    .map(({ row }) => row);
};

export const buildSpreadsheetRegionRowMap = ({
  rowCount,
  cells,
  regions,
  viewStates,
}: {
  rowCount: number;
  cells: IAiSpreadsheetCell[];
  regions: IAiSpreadsheetRegion[];
  viewStates: SpreadsheetRegionViewStates;
}) => {
  const validRegions = regions
    .filter((region) => region.validation.valid)
    .sort((left, right) => left.record_start_row - right.record_start_row);
  const byStartRow = new Map(
    validRegions.map((region) => [region.record_start_row, region]),
  );
  const result: number[] = [];

  for (let oneBasedRow = 1; oneBasedRow <= rowCount; oneBasedRow += 1) {
    const region = byStartRow.get(oneBasedRow);
    if (!region) {
      result.push(oneBasedRow - 1);
      continue;
    }

    result.push(...projectRegionRows(cells, region, viewStates[region.id]));
    oneBasedRow = Math.max(oneBasedRow, region.record_end_row);
  }

  return result;
};

export const projectAdaptedSpreadsheetSheet = (
  base: AdaptedSpreadsheetSheet,
  rowMap: number[],
): AdaptedSpreadsheetSheet => {
  const sourceToDisplayRow = new Map(
    rowMap.map((sourceRow, displayRow) => [sourceRow, displayRow]),
  );
  const sourceRow = (displayRow: number) =>
    getSavedSpreadsheetRow(rowMap, displayRow);

  return {
    ...base,
    rowCount: rowMap.length,
    isAddRowCell: (col, row) => base.isAddRowCell(col, sourceRow(row)),
    getCellContent: (col, row) =>
      base.getCellContent(col, sourceRow(row)) as GridCell,
    getCellsForSelection: (rect: Rectangle) =>
      Array.from({ length: rect.height }, (_, rowOffset) =>
        Array.from({ length: rect.width }, (_, colOffset) =>
          base.getCellContent(
            rect.x + colOffset,
            sourceRow(rect.y + rowOffset),
          ),
        ),
      ),
    getCellRecord: (col, row) => base.getCellRecord(col, sourceRow(row)),
    getManualRowCell: (row) => base.getManualRowCell(sourceRow(row)),
    getSearchMatches: (query) =>
      base.getSearchMatches(query).flatMap((match) => {
        const displayRow = sourceToDisplayRow.get(match.row);
        return displayRow == null ? [] : [{ ...match, row: displayRow }];
      }),
    getTextOverflow: (col, row) => base.getTextOverflow(col, sourceRow(row)),
    getRowHeight: (row) => base.getRowHeight(sourceRow(row)),
  };
};

export const hasActiveSpreadsheetColumnFilter = (
  filter: SpreadsheetColumnFilter | undefined,
) =>
  !!filter &&
  (filter.selectedValues !== null ||
    (filter.condition !== null && filter.conditionValue !== ""));

export const hasActiveSpreadsheetRegionView = (
  state: SpreadsheetRegionViewState | undefined,
) =>
  !!state &&
  (state.sort != null ||
    Object.values(state.filters).some(hasActiveSpreadsheetColumnFilter));
